/* Based on RFC 7946 ( https://tools.ietf.org/html/rfc7946 ) but not meant to be comprehensive. Extra
  tests are added as needed, e.g. to catch an actual failure that is causing bugs.

   Some of the validator functions may throw an "unimplemented" exception. If you see this, it means you
   are asking to validate a GeoJson type that no code has been written to validate it. So you can change
   the calling code or write the code to validate it.
*/

import { POLYGON_ARRAY_DEPTH, MULTIPOLYGON_ARRAY_DEPTH, MINIMUM_RING_VERTEX_COUNT } from 'geometry/geojson/constants';
import { getCoordinatesDepth } from 'geometry/geojson/convert';
import { isPolygonKinked } from 'geometry/geojson/kinks';
import { ringContainsAllVertexes } from 'geometry/geojson/ring';

const UNIMPLEMENTED_TYPE = 'type is unimplemented'; // See top-of-file comments.

let geoJsonTypeToValidator; // This gets set after all needed function declarations to avoid circular definition problems.

const _addFail = (failReason, validationFailures, validationContext) => {
  if (validationFailures) {
    const contextConcat = validationContext ? ` (${validationContext})` : '';
    validationFailures.push(`${failReason}${contextConcat}`);
  }
};

const _addContext = (context, additional) => context ? `${context}, ${additional}` : additional;

const _isValidVertex = (vertex) =>
  vertex && vertex.length === 2 && typeof vertex[0] === 'number' && typeof vertex[1] === 'number';

export const isInnerRingOob = (polygonCoordinates, ringI) =>
  !ringContainsAllVertexes(polygonCoordinates[0], polygonCoordinates[ringI]);

export const getOobInnerRingIndex = (polygonCoordinates) => {
  let oobRingI = null;
  polygonCoordinates.some((ring, ringI) => {
    if (ringI === 0) { return false; }
    if (isInnerRingOob(polygonCoordinates, ringI)) { oobRingI = ringI; }
    return (oobRingI !== null);
  });
  return oobRingI;
};

const _areAllRingVertexesValid = (ring, failures, context) => {
  const areValid = !ring.some(vertex => !_isValidVertex(vertex));
  if (!areValid && failures) {
    ring.forEach((vertex, vertexI) => {
      if (!_isValidVertex(vertex)) {
        const vertexContext = _addContext(context, `vertex #${vertexI}`);
        _addFail(`Invalid vertex - ${JSON.stringify(vertex)}.`, failures, vertexContext);
      }
    });
  }
  return areValid;
};

const _isRingClosed = (ring, failures, context) => {
  const isClosed = ring[0][0] === ring[ring.length-1][0] && ring[0][1] === ring[ring.length-1][1];
  if (!isClosed) { _addFail(`ring is not closed.`, failures, context); }
  return isClosed;
};

const _doesRingHaveEnoughCoords = (ring, failures, context) => {
  const isEnough = ring.length >= MINIMUM_RING_VERTEX_COUNT;
  if (!isEnough) { _addFail(`ring only has ${ring.length} vertexes.`, failures, context); }
  return isEnough;
};

const _isRingKinked = (ring, failures, context) => {
  const isKinked = isPolygonKinked({type:'Polygon', coordinates:[ring]});
  if (isKinked) { _addFail('ring is kinked', failures, context); }
  return isKinked;
};

/* Be careful about rearranging the order of _private function calls here. Generally, the called functions
   assume "valid" returns from previous functions. */
const _arePolygonRingsValid = (polygonCoordinates, failures, context) => {
  if (!polygonCoordinates.length) {
    _addFail('polygon has no rings', failures, context);
    return false;
  }
  return !polygonCoordinates.some((ring, ringI) => { // Return true below for an invalid condition.
    const ringContext = _addContext(context, `ring #${ringI}`);
    if (!_doesRingHaveEnoughCoords(ring, failures, ringContext)
      || !_isRingClosed(ring, failures, ringContext)
      || !_areAllRingVertexesValid(ring, failures, ringContext)
      || _isRingKinked(ring, failures, ringContext)) { return true; }
    if (ringI === 0) { return false; }
    const isOob = !ringContainsAllVertexes(polygonCoordinates[0], ring);
    if (isOob) { _addFail('ring is OOB of exterior ring', failures, ringContext); }
    return isOob;
  });
};

/** @param geoJson Object to check.
    @param [failures] Array that will be returned with debug errors describing validation failures. For
      better performance, leave undefined.
    @return Boolean True if it is a valid Polygon as far as implemented tests can confirm, false if not.
    @throws Error if called for a type for which checks are not implemented. */
export const isValidPolygon = (geoJson, failures) => {
  try {
    if (!geoJson) {
      _addFail('null param', failures);
      return false;
    }
    if (!geoJson.coordinates) {
      _addFail('missing .coordinates', failures);
      return false;
    }
    const depth = getCoordinatesDepth(geoJson.coordinates);
    if (depth !== POLYGON_ARRAY_DEPTH) {
      _addFail(`unexpected coordinate depth of ${depth}`, failures);
      return false;
    }
    return _arePolygonRingsValid(geoJson.coordinates, failures);
  } catch(error) {
    _addFail(error.message, failures);
    return false;
  }
};

/** @param geoJson Object to check.
    @param [failures] Array that will be returned with debug errors describing validation failures. For
      better performance, leave undefined.
    @return Boolean True if it is a valid MultiPolygon as far as implemented tests can confirm, false if not.
    @throws Error if called for a type for which checks are not implemented. */
export const isValidMultiPolygon = (geoJson, failures) => {
  try {
    if (!geoJson) {
      _addFail('null param', failures);
      return false;
    }
    if (!geoJson.coordinates) {
      _addFail('missing .coordinates', failures);
      return false;
    }
    const depth = getCoordinatesDepth(geoJson.coordinates);
    if (depth !== MULTIPOLYGON_ARRAY_DEPTH) {
      _addFail(`unexpected coordinate depth of ${depth}`, failures);
      return false;
    }
    return !geoJson.coordinates.some((polygonCoordinates, polygonI) =>
      !_arePolygonRingsValid(polygonCoordinates, failures, `polygon #${polygonI}`));
  } catch(err) {
    _addFail(err.message, failures);
    return false;
  }
};

/** @param geoJson Object to check.
    @param [failures] Array that will be returned with debug errors describing validation failures. For
      better performance, leave undefined.
    @return Boolean True if it is a valid Polygon or MultiPolygon as far as implemented tests can confirm, false if not.
    @throws Error if called for a type for which checks are not implemented. */
export const isValidXPolygon = (geoJson, failures) => {
  const type = geoJson && geoJson.type;
  if (type === 'Polygon') {
    return isValidPolygon(geoJson, failures);
  } else if (type === 'MultiPolygon') {
    return isValidMultiPolygon(geoJson, failures);
  } else {
    _addFail('Not Polygon or MultiPolygon geoJson type.', failures);
    return false;
  }
};

export const _isValidFeature = (geoJson, failures, context) => {
  try {
    if (!geoJson) {
      _addFail('null param', failures, context);
      return false;
    }
    const geometry = geoJson.geometry;
    if (!geometry) {
      _addFail('missing .geometry', failures, context);
      return false;
    }

    if (!geometry.type) {
      _addFail('missing .geometry.type', failures, context);
      return false;
    }

    const validator = geoJsonTypeToValidator[geometry.type];
    if (!validator) { throw new Error(UNIMPLEMENTED_TYPE); }

    if (!geoJson.properties || typeof geoJson.properties !== 'object') {
      _addFail('invalid/missing .properties', failures, context);
      return false;
    }

    return validator(geometry, failures, context);
  } catch(err) {
    _addFail(err.message, failures, context);
    if (err.message === UNIMPLEMENTED_TYPE) { throw err; }
    return false;
  }
};

/** @param geoJson Object to check.
    @param [failures] Array that will be returned with debug errors describing validation failures. For
      better performance, leave undefined.
    @return True if it is a valid Feature as far as implemented tests can confirm, false if not.
    @throws Error if called for a type for which checks are not implemented, including .geometry member of a Feature. */
export const isValidFeature = (geoJson, failures) => _isValidFeature(geoJson, failures);

/** @param geoJson Object to check.
    @param [failures] Array that will be returned with debug errors describing validation failures. For
      better performance, leave undefined.
    @return Boolean True if it is a valid FeatureCollection as far as implemented tests can confirm, false if not.
    @throws Error if called for a type for which checks are not implemented, including child members. */
export const isValidFeatureCollection = (geoJson, failures) => {
  try {
    if (!geoJson) {
      _addFail('null param', failures);
      return false;
    }
    if (!geoJson.features) {
      _addFail('missing .features', failures);
      return false;
    }
    return !geoJson.features.some((feature, featureI) => !_isValidFeature(feature, failures, `feature #${featureI}`));
  } catch(err) {
    if (err.message === UNIMPLEMENTED_TYPE) { // _isValidFeature() already added message to failures when it threw.
      throw err;
    } else {
      _addFail(err.message, failures);
    }
    return false;
  }
};

geoJsonTypeToValidator = { // Keep this definition right before isValidGeoJson() definition.
  'Feature': isValidFeature,
  'FeatureCollection': isValidFeatureCollection,
  'Polygon': isValidPolygon,
  'MultiPolygon': isValidMultiPolygon
};

/** Validate any kind of geoJson. It's better to call one of the other validators directly if you have
    a more limited set of criteria.
    @param geoJson Object to check.
    @param [failures] Array that will be returned with debug errors describing validation failures. For
      better performance, leave undefined.
    @return Boolean True if it is a valid geoJson object as far as implemented tests can confirm, false if not.
    @throws Error if called for a type for which checks are not implemented. */
export const isValidGeoJson = (geoJson, failures) => {
  if (!geoJson || !geoJson.type) { return false; }
  const validator = geoJsonTypeToValidator[geoJson.type];
  if (!validator) {
    _addFail(UNIMPLEMENTED_TYPE, failures);
    throw new Error(UNIMPLEMENTED_TYPE); // See top-of-file comments.
  }
  return validator(geoJson, failures); // Validators only throw for unimplemented type.
};
